
import Vue from 'vue';
import { TranslateResult } from 'vue-i18n';
import { ExtendedProfile, PianoCustomerProfile } from '@piano/types/pianoState';
import { faUserCircle, faGift, faEnvelope, faBookmark, faHistory, faRightFromBracket, faBell } from '@delfi.lt/fa-kit/icons/modules/classic/light';

interface Props {
  data: {
    isLoggedIn: boolean;
    profile: PianoCustomerProfile | null;
    extendedProfile: ExtendedProfile | null;
  };
  unreadCount: number;
  openLoginModal: () => void;
  logout: () => void;
}

interface Computed {
  title: string;
  hasChannelSubscription: boolean;
  subscription: Record<string, any> | false;
  isLoggedIn: boolean;
  list: {
    class?: string;
    type?: string;
    ordering?: boolean;
    content?: {
      class?: string;
      target?: string;
      href: string | TranslateResult;
      icon?: any;
      text: string | TranslateResult;
    };
    showForLoggedIn?: boolean;
    disabled?: boolean;
  }[];
  locale: string;
  hideOrdering: boolean;
  subscriptionUrl: string;
}

interface Methods {
  handleLogin: () => void;
  handleLogout: () => void;
}
interface Data {
  faRightFromBracket: typeof faRightFromBracket;
}

export default Vue.extend<Data, Methods, Computed, Props>({
  props: {
    data: {
      type: Object,
      required: true,
    },
    unreadCount: {
      type: Number,
      default: 0,
    },
    openLoginModal: {
      type: Function,
      required: true,
    },
    logout: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      faRightFromBracket,
    };
  },
  computed: {
    list() {
      const list = [
        {
          type: 'profile',
          content: {
            href: this.$t('login.routes.profile'),
            icon: faUserCircle,
            target: '_self',
            text: this.$t('login.profile'),
          },
          showForLoggedIn: true,
          disabled: false,
        },
        {
          type: 'subscriptions',
          content: {
            href: this.$t('login.routes.subscriptions'),
            target: '_self',
            text: this.$t('login.subscriptions'),
          },
          showForLoggedIn: true,
          disabled: false,
        },
        {
          type: 'notifications',
          content: {
            href: this.$t('login.routes.notifications'),
            icon: faBell,
            text: this.$t('login.notifications'),
          },
          showForLoggedIn: true,
          disabled: this.locale === 'ru_LT',
        },
        {
          type: 'loyalty',
          content: {
            href: this.$t('login.routes.loyalty'),
            icon: faGift,
            text: this.$t('login.loyalty'),
          },
          showForLoggedIn: true,
          disabled: this.locale === 'ru_LT',
        },
        {
          type: 'newsletters',
          content: {
            href: this.$t('login.routes.newsletters'),
            icon: faEnvelope,
            text: this.$t('login.newsletters'),
          },
          showForLoggedIn: true,
          disabled: this.locale === 'ru_LT',
        },
        {
          type: 'bookmarks',
          content: {
            href: this.$t('login.routes.read_later'),
            icon: faBookmark,
            text: this.$t('login.read_later'),
          },
          showForLoggedIn: true,
          disabled: false,
        },
        {
          type: 'history',
          content: {
            href: this.$t('login.routes.history'),
            icon: faHistory,
            text: this.$t('login.history'),
          },
          showForLoggedIn: true,
          disabled: false,
        },
        {
          type: 'myDelfi',
          content: {
            href: this.$t('login.routes.my_delfi'),
            icon: [],
            text: this.$t('login.my_delfi'),
          },
          showForLoggedIn: true,
          disabled: this.locale === 'ru_LT',
        },
        {
          class: 'header-customer-menu__item-content--separator display-none display-lg-block',
        },
      ];

      for (const item of list) {
        if (!item.disabled && item.showForLoggedIn) {
          item.disabled = !this.isLoggedIn;
        }
      }

      return list;
    },
    locale() {
      const { locale } = this.$channelConfig('settings');

      return locale;
    },
    title() {
      return `${this.$t('login.hello')}`;
    },
    hasChannelSubscription() {
      return this.$store.state.piano.access.channelAccess;
    },
    subscription() {
      if (!this.hasChannelSubscription) {
        return false;
      }

      return this.$store.state.piano.subscriptionInfo;
    },
    isLoggedIn() {
      return this.data.isLoggedIn;
    },
    hideOrdering() {
      const { specialFeatures } = this.$channelConfig('settings').application;
      return specialFeatures.hideSignupAndOrders.enabled;
    },
    subscriptionUrl() {
      const { misc } = this.$channelConfig('header').navigation;

      return misc.orderButton?.href;
    },
  },
  methods: {
    handleLogin() {
      this.$event.send('link-click', { name: 'Prisijungti', type: `customer-menu` });
      this.$store.commit('navigation/setHeaderAction', { type: 'profileInfo', value: false });
      this.openLoginModal();
    },
    handleLogout() {
      this.$event.send('link-click', { name: 'Atsijungti', type: `customer-menu` });
      this.$store.commit('navigation/setHeaderAction', { type: 'profileInfo', value: false });
      this.logout();
    },
  },
});
